import axios from "axios";
import { getCsrfTokenFromCookies, setCsrfTokenToCookies } from "../utils/csrf";
import store from "../state/store";
import { LOGOUT } from "../state/actions/types";
import { triggerSessionExpired } from "../utils/sessionExpired";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_VERSION = process.env.REACT_APP_API_VERSION;

export const API_BASE_URL = `${BASE_URL}${API_VERSION}`;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

let isRefreshing = false;
let refreshPromise = null;
let isLoggingOut = false;

const logoutUser = () => {
  if (isLoggingOut) return;

  isLoggingOut = true;

  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");

  delete api.defaults.headers.common.Authorization;

  store.dispatch({ type: LOGOUT });

  triggerSessionExpired();
};

const refreshCsrfToken = async () => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/auth/refresh-csrf`,
      { withCredentials: true }
    );

    const csrfToken = response.data?.data?.csrf_token;

    if (!csrfToken) return null;

    setCsrfTokenToCookies(csrfToken);

    api.defaults.headers.common["X-CSRF-Token"] = csrfToken;

    return csrfToken;
  } catch {
    return null;
  }
};

const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem("refresh_token");

    if (!refreshToken) return null;

    const response = await axios.post(
      `${API_BASE_URL}/auth/refresh-token`,
      { refresh_token: refreshToken },
      { withCredentials: true }
    );

    // Support both response structures used by the API.
    const data = response.data?.data ?? response.data;

    const newAccessToken = data?.access_token;
    const newRefreshToken = data?.refresh_token;

    if (!newAccessToken) return null;

    localStorage.setItem("access_token", newAccessToken);

    if (newRefreshToken) {
      localStorage.setItem("refresh_token", newRefreshToken);
    }

    api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;

    return newAccessToken;
  } catch {
    return null;
  }
};

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    const csrfToken = getCsrfTokenFromCookies();

    config.headers = config.headers || {};

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (csrfToken) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response || !originalRequest) {
      return Promise.reject(error);
    }

    const status = error.response.status;
    const url = originalRequest.url || "";

    const isRefreshEndpoint = url.includes("/auth/refresh-token");
    const isCsrfEndpoint = url.includes("/auth/refresh-csrf");

    // A failed refresh must end the session instead of trying to refresh again.
    if (isRefreshEndpoint) {
      logoutUser();
      return Promise.reject(error);
    }

    // Never retry authentication endpoints or an already retried request.
    if (originalRequest._retry || isCsrfEndpoint) {
      return Promise.reject(error);
    }

    const csrfExpired =
      status === 403 &&
      error.response.data?.code === "csrf_token_mismatch";

    const accessExpired = status === 401;

    if (!csrfExpired && !accessExpired) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      // Share one refresh operation between simultaneous failed requests.
      if (!isRefreshing) {
        isRefreshing = true;

        refreshPromise = (async () => {
          try {
            // Refresh CSRF and access tokens.
            const newCsrfToken = await refreshCsrfToken();
            const newAccessToken = await refreshAccessToken();

            return { newCsrfToken, newAccessToken };
          } finally {
            isRefreshing = false;
            refreshPromise = null;
          }
        })();
      }

      const { newAccessToken, newCsrfToken } = await refreshPromise;

      // If access-token refresh fails, the session cannot continue.
      if (!newAccessToken) {
        logoutUser();
        return Promise.reject(error);
      }

      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      if (newCsrfToken) {
        originalRequest.headers["X-CSRF-Token"] = newCsrfToken;
      }

      return api(originalRequest);
    } catch (refreshError) {
      logoutUser();
      return Promise.reject(refreshError);
    }
  }
);

export default api;